import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  deleteAnnouncement, giveKudos, loadAnnouncements, loadColleagues, loadKudos, loadMyReads, loadMySurveys, loadSurveyResults, loadSurveys,
  markRead, saveAnnouncement, saveSurvey, submitSurvey, type Announcement, type Colleague, type Kudos, type Survey, type SurveyQ, type SurveyResult,
} from "../data";
import type { Workspace } from "../types";

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
export const KUDOS_VALUES = ["teamwork", "customer", "ownership", "innovation", "excellence", "helpfulness"];
type Tab = "news" | "surveys" | "kudos";

/** Company news, pulse surveys and peer recognition, for everyone. HR posts and builds surveys. */
export function Engagement({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>("news");
  return (
    <div className="page">
      <div className="segmented" role="tablist" aria-label={t("navEngagement")}>
        {(["news", "surveys", "kudos"] as Tab[]).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "current" : ""} onClick={() => setTab(k)}>{t(`engTab_${k}` as StringKey)}</button>
        ))}
      </div>
      {tab === "news" && <News ws={ws} />}
      {tab === "surveys" && <Surveys ws={ws} />}
      {tab === "kudos" && <Recognition ws={ws} />}
    </div>
  );
}

function News({ ws }: { ws: Workspace }) {
  const { t, lang } = useI18n();
  const cid = ws.membership.company_id;
  const hr = ws.permissions.has("engagement.manage");
  const [rows, setRows] = useState<Announcement[] | null>(null);
  const [reads, setReads] = useState<Set<string>>(new Set());
  const [edit, setEdit] = useState<Partial<Announcement> | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const refresh = () => Promise.all([loadAnnouncements(cid), loadMyReads()]).then(([a, r]) => { setRows(a); setReads(new Set(r)); }).catch((e) => setMsg(e.message));
  useEffect(() => { void refresh(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const day = (x: string) => new Date(x).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", { day: "numeric", month: "short", year: "numeric" });
  const live = (rows ?? []).filter((a) => hr || !a.expires_on || a.expires_on >= new Date().toISOString().slice(0, 10));
  async function save(e: FormEvent) {
    e.preventDefault();
    if (!edit) return;
    try { await saveAnnouncement(cid, { id: edit.id, title: edit.title, body: edit.body, pinned: !!edit.pinned, expires_on: edit.expires_on || null }); setEdit(null); await refresh(); }
    catch (x) { setMsg(errText(x, t("errorGeneric"))); }
  }
  async function read(id: string) { await markRead(id).catch(() => undefined); setReads(new Set([...reads, id])); }
  return (
    <>
      {msg && <p className="alert danger" role="alert">{msg}</p>}
      {hr && !edit && <div className="actions start"><button type="button" className="btn primary" onClick={() => setEdit({ title: "", body: "", pinned: false })}>{t("annNew")}</button></div>}
      {edit && (
        <form className="card" onSubmit={save}>
          <h2>{edit.id ? t("annEdit") : t("annNew")}</h2>
          <label className="field"><span>{t("rwTitle")}</span><input value={edit.title ?? ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })} required /></label>
          <label className="field"><span>{t("annBody")}</span><textarea rows={5} value={edit.body ?? ""} onChange={(e) => setEdit({ ...edit, body: e.target.value })} required /></label>
          <div className="field-row">
            <label className="field"><span>{t("annExpires")}</span><input type="date" value={edit.expires_on ?? ""} onChange={(e) => setEdit({ ...edit, expires_on: e.target.value })} /></label>
            <label className="check"><input type="checkbox" checked={!!edit.pinned} onChange={(e) => setEdit({ ...edit, pinned: e.target.checked })} /> {t("annPinned")}</label>
          </div>
          <div className="actions"><button type="button" className="btn secondary" onClick={() => setEdit(null)}>{t("cancel")}</button><button className="btn primary">{t("annPublish")}</button></div>
        </form>
      )}
      {rows === null ? <p className="muted">{t("loading")}</p> : live.length === 0 ? <p className="muted">{t("annNone")}</p> : live.map((a) => {
        const unread = !reads.has(a.id);
        return (
          <article key={a.id} className={`card announcement${unread ? " unread" : ""}`}>
            <div className="row spread">
              <h2>{a.pinned && <span className="badge info">{t("annPinned")}</span>} {a.title} {unread && <span className="badge warning">{t("annNewBadge")}</span>}</h2>
              <span className="muted small-note">{day(a.published_at)}{a.expires_on ? ` · ${t("annUntil")} ${a.expires_on}` : ""}</span>
            </div>
            <p className="pre-line">{a.body}</p>
            <div className="actions start">
              {unread && <button type="button" className="btn secondary small" onClick={() => read(a.id)}>{t("annMarkRead")}</button>}
              {hr && <button type="button" className="link" onClick={() => setEdit(a)}>{t("edit")}</button>}
              {hr && <button type="button" className="link danger" onClick={() => { if (window.confirm(t("annDeleteConfirm"))) void deleteAnnouncement(a.id).then(refresh); }}>{t("delete")}</button>}
            </div>
          </article>
        );
      })}
    </>
  );
}

function Surveys({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const cid = ws.membership.company_id;
  const hr = ws.permissions.has("engagement.manage");
  const [mine, setMine] = useState<(Survey & { answered: boolean })[]>([]);
  const [all, setAll] = useState<Survey[]>([]);
  const [take, setTake] = useState<Survey | null>(null);
  const [results, setResults] = useState<{ s: Survey; r: SurveyResult } | null>(null);
  const [build, setBuild] = useState<Partial<Survey> | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = async () => {
    setMine(await loadMySurveys(cid).catch(() => []));
    if (hr) setAll(await loadSurveys(cid).catch(() => []));
  };
  useEffect(() => { void refresh(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const todo = mine.filter((s) => s.status === "open" && !s.answered);
  async function run(fn: () => Promise<unknown>, done?: string) {
    setMsg(null);
    try { await fn(); await refresh(); if (done) setMsg({ ok: true, text: done }); }
    catch (x) { setMsg({ ok: false, text: errText(x, t("errorGeneric")) }); }
  }
  return (
    <>
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      {take ? <TakeSurvey s={take} onCancel={() => setTake(null)} onDone={() => { setTake(null); void run(async () => undefined, t("svThanks")); }} /> : (
        <section className="card">
          <h2>{t("svForYou")}</h2>
          {todo.length === 0 ? <p className="muted">{t("svNoneOpen")}</p> : (
            <ul className="req-list">{todo.map((s) => <li key={s.id}><span><strong>{s.title}</strong>{s.closes_on && <span className="muted small-note"> · {t("svCloses")} {s.closes_on}</span>}{s.anonymous && <span className="badge neutral"> {t("svAnonymous")}</span>}</span>
              <button type="button" className="btn primary small" onClick={() => setTake(s)}>{t("svAnswer")}</button></li>)}</ul>
          )}
          {mine.some((s) => s.answered) && <p className="muted small-note">{t("svAnsweredN").replace("{n}", String(mine.filter((s) => s.answered).length))}</p>}
        </section>
      )}
      {results && <Results s={results.s} r={results.r} onClose={() => setResults(null)} />}
      {hr && (
        <section className="card">
          <div className="row spread"><h2>{t("svManage")}</h2>{!build && <button type="button" className="btn secondary small" onClick={() => setBuild({ title: "", intro: "", anonymous: true, status: "draft", questions: [] })}>{t("svNew")}</button>}</div>
          {build && <Builder s={build} onCancel={() => setBuild(null)} onSave={(s) => run(() => saveSurvey(cid, s), t("saved")).then(() => setBuild(null))} />}
          <div className="table-wrap"><table>
            <thead><tr><th>{t("rwTitle")}</th><th>{t("status")}</th><th className="hide-sm">{t("svQuestions")}</th><th /></tr></thead>
            <tbody>{all.map((s) => (
              <tr key={s.id}>
                <td>{s.title}{s.anonymous && <span className="muted small-note"> · {t("svAnonymous")}</span>}</td>
                <td><span className={`badge ${s.status === "open" ? "success" : s.status === "draft" ? "warning" : "neutral"}`}>{t(`svStatus_${s.status}` as StringKey)}</span></td>
                <td className="hide-sm mono">{s.questions.length}</td>
                <td className="actions">
                  {s.status === "draft" && <><button type="button" className="link" onClick={() => setBuild(s)}>{t("edit")}</button>
                    <button type="button" className="btn secondary small" disabled={s.questions.length === 0} onClick={() => run(() => saveSurvey(cid, { id: s.id, status: "open" }), t("svLaunched"))}>{t("svLaunch")}</button></>}
                  {s.status === "open" && <button type="button" className="btn secondary small" onClick={() => run(() => saveSurvey(cid, { id: s.id, status: "closed" }))}>{t("svCloseBtn")}</button>}
                  {s.status !== "draft" && <button type="button" className="link" onClick={() => run(async () => setResults({ s, r: await loadSurveyResults(s.id) }))}>{t("svResults")}</button>}
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        </section>
      )}
    </>
  );
}

function TakeSurvey({ s, onCancel, onDone }: { s: Survey; onCancel: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [a, setA] = useState<Record<string, string | number>>({});
  const [err, setErr] = useState<string | null>(null);
  const scale = (q: SurveyQ) => (q.kind === "enps" ? Array.from({ length: 11 }, (_, i) => i) : [1, 2, 3, 4, 5]);
  async function send(e: FormEvent) {
    e.preventDefault();
    const missing = s.questions.find((q) => q.kind !== "text" && a[q.id] === undefined);
    if (missing) { setErr(`${t("svMissing")}: ${missing.text}`); return; }
    try { await submitSurvey(s.id, a); onDone(); } catch (x) { setErr(errText(x, t("errorGeneric"))); }
  }
  return (
    <form className="card" onSubmit={send}>
      <h2>{s.title}</h2>
      {s.intro && <p className="muted">{s.intro}</p>}
      <p className="alert info">{s.anonymous ? t("svAnonNote") : t("svNamedNote")}</p>
      {s.questions.map((q, i) => (
        <fieldset key={q.id} className="survey-q">
          <legend>{i + 1}. {q.text}</legend>
          {(q.kind === "scale" || q.kind === "enps") && (
            <div className="scale" role="radiogroup" aria-label={q.text}>
              {scale(q).map((n) => <button key={n} type="button" role="radio" aria-checked={a[q.id] === n} className={a[q.id] === n ? "current" : ""} onClick={() => setA({ ...a, [q.id]: n })}>{n}</button>)}
              <span className="muted small-note scale-ends">{q.kind === "enps" ? t("svEnpsEnds") : t("svScaleEnds")}</span>
            </div>
          )}
          {q.kind === "choice" && (q.options ?? []).map((o) => <label key={o} className="check"><input type="radio" name={q.id} checked={a[q.id] === o} onChange={() => setA({ ...a, [q.id]: o })} /> {o}</label>)}
          {q.kind === "text" && <textarea rows={3} aria-label={q.text} value={(a[q.id] as string) ?? ""} onChange={(e) => setA({ ...a, [q.id]: e.target.value })} />}
        </fieldset>
      ))}
      {err && <p className="alert danger" role="alert">{err}</p>}
      <div className="actions"><button type="button" className="btn secondary" onClick={onCancel}>{t("cancel")}</button><button className="btn primary">{t("svSubmit")}</button></div>
    </form>
  );
}

function Builder({ s, onCancel, onSave }: { s: Partial<Survey>; onCancel: () => void; onSave: (s: Partial<Survey>) => void }) {
  const { t } = useI18n();
  const [f, setF] = useState<Partial<Survey>>(s);
  const qs = f.questions ?? [];
  const setQ = (i: number, q: Partial<SurveyQ>) => setF({ ...f, questions: qs.map((x, k) => (k === i ? { ...x, ...q } : x)) });
  const add = (kind: SurveyQ["kind"]) => setF({ ...f, questions: [...qs, { id: `q${Date.now().toString(36)}`, kind, text: "", ...(kind === "choice" ? { options: [] } : {}) }] });
  return (
    <form className="stack builder" onSubmit={(e) => { e.preventDefault(); onSave({ id: f.id, title: f.title, intro: f.intro || null, anonymous: !!f.anonymous, closes_on: f.closes_on || null, questions: qs.filter((q) => q.text.trim()) }); }}>
      <div className="field-row">
        <label className="field"><span>{t("rwTitle")}</span><input value={f.title ?? ""} onChange={(e) => setF({ ...f, title: e.target.value })} required /></label>
        <label className="field"><span>{t("svCloses")}</span><input type="date" value={f.closes_on ?? ""} onChange={(e) => setF({ ...f, closes_on: e.target.value })} /></label>
      </div>
      <label className="field"><span>{t("svIntro")}</span><input value={f.intro ?? ""} onChange={(e) => setF({ ...f, intro: e.target.value })} /></label>
      <label className="check"><input type="checkbox" checked={!!f.anonymous} disabled={!!f.id} onChange={(e) => setF({ ...f, anonymous: e.target.checked })} /> {t("svAnonLong")}</label>
      <ol className="stack">{qs.map((q, i) => (
        <li key={q.id} className="row">
          <span className="badge neutral">{t(`svKind_${q.kind}` as StringKey)}</span>
          <input aria-label={t("svQuestion")} placeholder={t("svQuestion")} value={q.text} onChange={(e) => setQ(i, { text: e.target.value })} />
          {q.kind === "choice" && <input aria-label={t("svOptions")} placeholder={t("svOptions")} value={(q.options ?? []).join(", ")} onChange={(e) => setQ(i, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />}
          <button type="button" className="link danger" onClick={() => setF({ ...f, questions: qs.filter((_, k) => k !== i) })}>{t("remove")}</button>
        </li>
      ))}</ol>
      <div className="actions start">{(["scale", "enps", "choice", "text"] as const).map((k) => <button key={k} type="button" className="btn secondary small" onClick={() => add(k)}>+ {t(`svKind_${k}` as StringKey)}</button>)}</div>
      <div className="actions"><button type="button" className="btn secondary" onClick={onCancel}>{t("cancel")}</button><button className="btn primary">{t("save")}</button></div>
    </form>
  );
}

function Results({ s, r, onClose }: { s: Survey; r: SurveyResult; onClose: () => void }) {
  const { t } = useI18n();
  return (
    <section className="card">
      <div className="row spread"><h2>{s.title}</h2><button type="button" className="link" onClick={onClose}>{t("close")}</button></div>
      <p className="muted">{t("svRate").replace("{n}", String(r.responses)).replace("{m}", String(r.invited))}</p>
      {r.hidden ? <p className="alert info">{t("svHidden")}</p> : r.questions.map((q) => (
        <div key={q.id} className="survey-q">
          <h3 className="sub-head">{q.text}</h3>
          {q.kind === "enps" && <p><span className="stat-value">{q.enps ?? "—"}</span> <span className="muted small-note">eNPS · {t("svEnpsHelp")}</span></p>}
          {q.kind === "scale" && <p><span className="stat-value">{q.average ?? "—"}</span> <span className="muted small-note">/ 5</span></p>}
          {(q.spread || q.counts) && <Bars data={(q.spread ?? q.counts)!} />}
          {q.by_department && Object.keys(q.by_department).length > 0 && <p className="muted small-note">{t("svByDept")}: {Object.entries(q.by_department).map(([d, v]) => `${d} ${v}`).join(" · ")}</p>}
          {q.comments && (q.comments.length ? <ul className="comments">{q.comments.map((c, i) => <li key={i}>“{c}”</li>)}</ul> : <p className="muted">—</p>)}
        </div>
      ))}
    </section>
  );
}

function Bars({ data }: { data: Record<string, number> }) {
  const rows = Object.entries(data);
  const max = Math.max(1, ...rows.map((x) => x[1]));
  return <ul className="hbars">{rows.map(([k, n]) => <li key={k}><span>{k}</span><span className="track"><span className="fill" style={{ width: `${(100 * n) / max}%` }} /></span><span className="n">{n}</span></li>)}</ul>;
}

function Recognition({ ws }: { ws: Workspace }) {
  const { t, pick, lang } = useI18n();
  const cid = ws.membership.company_id;
  const [feed, setFeed] = useState<Kudos[] | null>(null);
  const [people, setPeople] = useState<Colleague[]>([]);
  const [f, setF] = useState({ to: "", value: "teamwork", message: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = () => loadKudos(cid).then(setFeed).catch((e) => setMsg({ ok: false, text: e.message }));
  useEffect(() => { void refresh(); void loadColleagues(cid).then(setPeople).catch(() => setPeople([])); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const name = (id: string) => { const p = people.find((x) => x.employment_id === id); return p ? pick(p.full_name, p.full_name_ar) : "—"; };
  const canGive = !!ws.membership.employment_id;
  async function send(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try { await giveKudos(cid, f.to, f.value, f.message); setF({ ...f, to: "", message: "" }); setMsg({ ok: true, text: t("kdSent") }); await refresh(); }
    catch (x) { setMsg({ ok: false, text: errText(x, t("errorGeneric")) }); }
  }
  const day = (x: string) => new Date(x).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", { day: "numeric", month: "short" });
  return (
    <>
      {canGive && (
        <form className="card" onSubmit={send}>
          <h2>{t("kdGive")}</h2>
          <div className="field-row">
            <label className="field"><span>{t("kdTo")}</span><select value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} required><option value="">—</option>
              {people.filter((p) => p.employment_id !== ws.membership.employment_id).map((p) => <option key={p.employment_id} value={p.employment_id}>{pick(p.full_name, p.full_name_ar)}{p.department_name ? ` · ${pick(p.department_name, p.department_name_ar)}` : ""}</option>)}</select></label>
            <label className="field"><span>{t("kdValue")}</span><select value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })}>{KUDOS_VALUES.map((v) => <option key={v} value={v}>{t(`kdV_${v}` as StringKey)}</option>)}</select></label>
          </div>
          <label className="field"><span>{t("kdMessage")}</span><input value={f.message} maxLength={280} onChange={(e) => setF({ ...f, message: e.target.value })} required /></label>
          {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
          <div className="actions"><button className="btn primary">{t("kdSend")}</button></div>
        </form>
      )}
      <section className="card">
        <h2>{t("kdFeed")}</h2>
        {feed === null ? <p className="muted">{t("loading")}</p> : feed.length === 0 ? <p className="muted">{t("kdNone")}</p> : (
          <ul className="kudos-feed">{feed.map((k) => (
            <li key={k.id}>
              <p><strong>{name(k.from_employment)}</strong> → <strong>{name(k.to_employment)}</strong> <span className="badge success">{t(`kdV_${k.value}` as StringKey)}</span></p>
              <p>{k.message}</p>
              <span className="muted small-note">{day(k.created_at)}</span>
            </li>
          ))}</ul>
        )}
      </section>
    </>
  );
}

/** Latest unread or pinned company news for the home page. */
export function HomeNews({ ws, onMore }: { ws: Workspace; onMore: () => void }) {
  const { t } = useI18n();
  const [rows, setRows] = useState<Announcement[]>([]);
  const [reads, setReads] = useState<Set<string>>(new Set());
  useEffect(() => {
    void Promise.all([loadAnnouncements(ws.membership.company_id), loadMyReads()]).then(([a, r]) => { setRows(a); setReads(new Set(r)); }).catch(() => undefined);
  }, [ws.membership.company_id]);
  const today = new Date().toISOString().slice(0, 10);
  const show = rows.filter((a) => (!a.expires_on || a.expires_on >= today) && (a.pinned || !reads.has(a.id))).slice(0, 3);
  if (show.length === 0) return null;
  return (
    <section className="card">
      <div className="row spread"><h2>{t("annHome")}</h2><button type="button" className="link" onClick={onMore}>{t("seeAll")}</button></div>
      <ul className="req-list">{show.map((a) => <li key={a.id}><span><strong>{a.title}</strong><br /><span className="muted small-note">{a.body.length > 140 ? `${a.body.slice(0, 140)}…` : a.body}</span></span>{!reads.has(a.id) && <span className="badge warning">{t("annNewBadge")}</span>}</li>)}</ul>
    </section>
  );
}
